// =============================================================================
// MARKETING TOOLS COMPONENT - Admin Panel
// Generates Pinterest-ready promotional content with branded overlays
// =============================================================================

import { useState, useEffect } from 'react'
import { 
  Megaphone, Image, Sparkles, Download, RefreshCw, 
  Wand2, Layout, Clock,
  TrendingUp, Eye, Heart, CheckCircle, XCircle, Loader
} from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || ''

// =============================================================================
// PROMO TEMPLATES
// =============================================================================

const PROMO_TEMPLATES = [
  {
    id: 'before-after',
    name: 'Before/After',
    description: 'Show transformation from idea to pattern',
    textOverlay: 'From Idea → Pattern in 60 Seconds',
    subtext: 'zennago.com',
    style: 'split'
  },
  {
    id: 'showcase',
    name: 'Pattern Showcase',
    description: 'Beautiful pattern with call-to-action',
    textOverlay: 'Create Patterns Like This',
    subtext: 'AI-Powered • Instant Download • zennago.com',
    style: 'overlay'
  },
  {
    id: 'how-fast',
    name: 'Speed Demo',
    description: 'Emphasize how quick Zenna is',
    textOverlay: '⚡ 60 Seconds',
    subtext: 'From idea to craft-ready pattern',
    style: 'bold'
  },
  {
    id: 'craft-types',
    name: 'Multi-Craft',
    description: 'Show different craft outputs',
    textOverlay: 'One Design, Every Craft',
    subtext: 'Cross-stitch • Embroidery • Cricut • More',
    style: 'grid'
  },
  {
    id: 'testimonial',
    name: 'User Quote',
    description: 'Social proof style',
    textOverlay: '"I made this in under a minute!"',
    subtext: '— Zenna User • Try free at zennago.com',
    style: 'quote'
  },
  {
    id: 'free-trial',
    name: 'Free Trial CTA',
    description: 'Drive signups',
    textOverlay: '7 FREE Designs',
    subtext: 'No credit card • Start creating now',
    style: 'cta'
  }
]

// =============================================================================
// RANDOM PATTERN PROMPTS (for showcase generation)
// =============================================================================

const SHOWCASE_PROMPTS = [
  // Realistic Animals
  "realistic sleeping tabby cat curled up on soft blanket, detailed fur texture",
  "photorealistic golden retriever puppy portrait, warm lighting",
  "realistic majestic wolf howling at full moon, detailed fur",
  "lifelike monarch butterfly on purple lavender flower",
  "realistic hummingbird drinking from red flower, iridescent feathers",
  "detailed realistic owl portrait with piercing eyes",
  "photorealistic fox in autumn forest, orange fur detail",
  "realistic sea turtle swimming in clear blue water",
  
  // Stylized/Cute Animals
  "adorable kawaii style cat with big sparkly eyes",
  "cute cartoon bunny in flower garden, whimsical",
  "playful puppy with tongue out, charming illustration style",
  "sweet hedgehog holding tiny mushroom, storybook style",
  
  // Realistic Flowers & Nature
  "photorealistic red rose with morning dewdrops, macro detail",
  "realistic sunflower field at golden hour, warm tones",
  "detailed cherry blossom branch against blue sky",
  "lifelike peony bouquet in soft pink and white",
  "realistic succulent arrangement, vibrant greens",
  "photorealistic wildflower meadow at sunset",
  "detailed lotus flower floating on still water",
  "realistic lavender field in Provence style",
  
  // Stylized Nature
  "whimsical mushroom cottage in enchanted forest",
  "dreamy watercolor style mountain landscape",
  "cottage core aesthetic wildflower wreath",
  
  // Realistic Landscapes
  "photorealistic mountain lake reflection at sunrise",
  "realistic autumn forest path with fallen leaves",
  "detailed ocean wave crashing on rocky shore",
  "lifelike snowy cabin in winter woods",
  "realistic desert sunset with saguaro cactus silhouette",
  "photorealistic northern lights over mountain range",
  
  // Stylized Scenes
  "geometric mountain landscape, modern minimal style",
  "boho rainbow arches with abstract shapes",
  "celestial moon phases with stars, mystical design",
  "art deco style city skyline at night",
  
  // Seasonal - Realistic
  "realistic autumn maple leaves and pumpkins arrangement",
  "photorealistic spring cherry blossoms with soft bokeh",
  "detailed winter cardinal on snowy pine branch",
  "realistic summer beach sunset with palm silhouettes",
  
  // Seasonal - Stylized
  "cozy autumn scene with cute fox and falling leaves",
  "whimsical winter snowflake mandala design",
  "cheerful spring garden with dancing butterflies",
  
  // Hobbies & Interests - Realistic
  "realistic vintage sewing machine with colorful thread spools",
  "photorealistic steaming coffee cup with latte art heart",
  "detailed stack of antique leather-bound books",
  "realistic acoustic guitar with warm wood grain detail",
  "lifelike knitting needles with soft yarn balls",
  
  // Hobbies - Stylized
  "cozy reading nook illustration with cat and tea",
  "whimsical artist palette with paint splashes",
  
  // Home & Family
  "realistic cozy fireplace with stockings, warm glow",
  "detailed front porch with rocking chairs and flowers",
  "photorealistic baby hands holding parent finger",
  "lifelike wedding bouquet with roses and greenery",
  
  // Holidays - Realistic
  "photorealistic christmas tree with twinkling lights",
  "realistic carved pumpkin with candle glow inside",
  "detailed easter eggs in woven basket with spring flowers",
  "lifelike fourth of july fireworks over water reflection",
  
  // Holidays - Stylized
  "cute halloween black cat with witch hat",
  "charming gingerbread house with candy decorations",
  "whimsical valentine hearts and flowers arrangement",
  
  // Inspirational - Mixed Styles
  "realistic hands releasing butterfly into sunlight",
  "detailed compass on vintage map, adventure theme",
  "photorealistic dandelion seeds blowing in wind",
  "dreamy hot air balloons over lavender fields"
]

const CRAFT_TYPES = [
  { value: 'cross-stitch', label: 'Cross Stitch' },
  { value: 'embroidery', label: 'Embroidery' },
  { value: 'pixel-art', label: 'Pixel Art' },
  { value: 'pattern', label: 'General Pattern' },
  { value: 'quilting', label: 'Quilting' },
  { value: 'knitting', label: 'Knitting' },
  { value: 'crochet', label: 'Crochet' },
  { value: 'perler-beads', label: 'Perler Beads' },
  { value: 'diamond-painting', label: 'Diamond Painting' },
  { value: 'punch-needle', label: 'Punch Needle' },
  { value: 'latch-hook', label: 'Latch Hook' },
  { value: 'plastic-canvas', label: 'Plastic Canvas' }
]

// =============================================================================
// MULTI-CRAFT RANDOMIZATION
// =============================================================================

const MULTI_CRAFT_TYPES_FOR_VARIETY = [
  'cross-stitch', 'embroidery', 'knitting', 'crochet', 'macrame', 
  'punch-needle', 'latch-hook', 'quilting', 'beading', 'string-art',
  'stained-glass', 'mosaic', 'perler-beads', 'diamond-painting', 'applique'
]

function getRandomCraftTypes(count) {
  const shuffled = [...MULTI_CRAFT_TYPES_FOR_VARIETY].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MarketingTools({ adminEmail }) {
  const [activeSection, setActiveSection] = useState('generator')
  const [generating, setGenerating] = useState(false)
  const [generatingPromos, setGeneratingPromos] = useState(false)
  const [generatedImages, setGeneratedImages] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(PROMO_TEMPLATES[1])
  const [customPrompt, setCustomPrompt] = useState('')
  const [selectedCraftType, setSelectedCraftType] = useState('cross-stitch')
  const [batchSize, setBatchSize] = useState(4)
  const [posting, setPosting] = useState({})
  const [postResults, setPostResults] = useState({})
  const [generationStep, setGenerationStep] = useState('')
  
  // Stats
  const [stats, setStats] = useState({
    totalPosts: 0,
    thisWeek: 0,
    engagement: 0,
    topPlatform: 'pinterest'
  })

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/social/posts?limit=100`)
      if (res.ok) {
        const data = await res.json()
        const posts = data.posts || []
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        
        setStats({
          totalPosts: posts.length,
          thisWeek: posts.filter(p => new Date(p.created_at) > weekAgo).length,
          engagement: posts.filter(p => p.status === 'posted').length,
          topPlatform: 'pinterest'
        })
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  // =============================================================================
  // GENERATE BRANDED PROMO IMAGES
  // =============================================================================
  
  const generateBrandedPromos = async () => {
    setGenerating(true)
    setGeneratedImages([])
    setGenerationStep('Generating patterns...')
    
    try {
      const prompts = customPrompt 
  ? Array(batchSize).fill(customPrompt)
  : getRandomPrompts(batchSize)
      
      // Check if Multi-Craft template is selected
      const isMultiCraft = selectedTemplate.id === 'craft-types'
      
      // Get varied craft types if multi-craft, otherwise use selected
      const craftTypesToUse = isMultiCraft 
        ? getRandomCraftTypes(batchSize)
        : Array(batchSize).fill(selectedCraftType)
      
      const patternResults = []
      
      // Step 1: Generate raw pattern images
      for (let i = 0; i < prompts.length; i++) {
        const prompt = prompts[i]
        const craftType = craftTypesToUse[i] || selectedCraftType
        
        setGenerationStep(`Generating ${craftType} pattern ${i + 1}/${prompts.length}...`)
        
        const res = await fetch(`${API_URL}/chat/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `Generate a ${craftType} pattern: ${prompt}`,
            userEmail: adminEmail,
            conversationHistory: [],
            pastHistory: []
          })
        })
        
        if (res.ok) {
          const data = await res.json()
          if (data.images && data.images.length > 0) {
            // Get the first image URL
            const firstImage = data.images[0]
            const imageUrl = typeof firstImage === 'string' ? firstImage : (firstImage.url || firstImage)
            
            patternResults.push({
              patternUrl: imageUrl,
              prompt,
              craftType  // Store the craft type used
            })
          }
        }
        
        // Small delay between requests
        await new Promise(r => setTimeout(r, 500))
      }
      
      if (patternResults.length === 0) {
        throw new Error('No patterns were generated')
      }
      
      // Step 2: Create branded promo images from patterns
      setGenerationStep('Creating branded promo images...')
      setGeneratingPromos(true)
      
      const promoResults = []
      
      for (let i = 0; i < patternResults.length; i++) {
        const { patternUrl, prompt, craftType } = patternResults[i]
        setGenerationStep(`Branding image ${i + 1}/${patternResults.length}...`)
        
        try {
          const promoRes = await fetch(`${API_URL}/marketing/create-promo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              patternImageUrl: patternUrl,
              templateId: selectedTemplate.id
            })
          })
          
          if (promoRes.ok) {
            const promoData = await promoRes.json()
            
            promoResults.push({
              id: Date.now() + Math.random(),
              promoImageUrl: promoData.promoImageUrl,
              patternImageUrl: patternUrl,
              prompt,
              craftType,  // Use the varied craft type
              template: selectedTemplate
            })
          } else {
            // If branding fails, still show the raw pattern
            console.error('Branding failed, using raw pattern')
            promoResults.push({
              id: Date.now() + Math.random(),
              promoImageUrl: patternUrl, // Fallback to raw pattern
              patternImageUrl: patternUrl,
              prompt,
              craftType,
              template: selectedTemplate,
              isFallback: true
            })
          }
        } catch (promoError) {
          console.error('Promo creation error:', promoError)
          // Fallback to raw pattern
          promoResults.push({
            id: Date.now() + Math.random(),
            promoImageUrl: patternUrl,
            patternImageUrl: patternUrl,
            prompt,
            craftType,
            template: selectedTemplate,
            isFallback: true
          })
        }
        
        await new Promise(r => setTimeout(r, 300))
      }
      
      setGeneratedImages(promoResults)
      setGenerationStep('')
      
    } catch (err) {
      console.error('Generation error:', err)
      alert('Failed to generate promo images. Check console for details.')
      setGenerationStep('')
    } finally {
      setGenerating(false)
      setGeneratingPromos(false)
    }
  }

  const getRandomPrompts = (count) => {
    const shuffled = [...SHOWCASE_PROMPTS].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  }

  // =============================================================================
  // POST TO SOCIAL MEDIA
  // =============================================================================

  const postToSocial = async (image, platform) => {
    setPosting(prev => ({ ...prev, [image.id]: platform }))
    
    try {
      const caption = generateCaption(image, selectedTemplate)
      
      const res = await fetch(`${API_URL}/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: image.promoImageUrl,
          caption,
          craftType: image.craftType,
          link: 'https://zennago.com',
          platforms: [platform]
        })
      })
      
      const data = await res.json()
      
      setPostResults(prev => ({
        ...prev,
        [image.id]: {
          ...prev[image.id],
          [platform]: data.results?.[platform]?.success ? 'success' : 'failed'
        }
      }))
      
      if (data.results?.[platform]?.success) {
        fetchStats() // Refresh stats
      }
    } catch (err) {
      console.error('Post error:', err)
      setPostResults(prev => ({
        ...prev,
        [image.id]: { ...prev[image.id], [platform]: 'failed' }
      }))
    } finally {
      setPosting(prev => ({ ...prev, [image.id]: null }))
    }
  }

  const generateCaption = (image, template) => {
    const hashtags = '#crafting #diy #handmade #zennago #aiart #crossstitch #embroidery #cricut #makersgonnamake #craftlife'
    
    const captions = {
      'before-after': `✨ From idea to pattern in 60 seconds!\n\nZenna AI turns your imagination into craft-ready designs.\n\n🔗 Try free: zennago.com\n\n${hashtags}`,
      'showcase': `🎨 Look what Zenna created!\n\n${image.prompt}\n\nReady for ${image.craftType}. Make yours at zennago.com ✨\n\n${hashtags}`,
      'how-fast': `⚡ 60 SECONDS.\n\nThat's all it takes to turn any idea into a beautiful ${image.craftType} pattern.\n\nNo design skills needed. Just describe what you want.\n\n🔗 zennago.com\n\n${hashtags}`,
      'craft-types': `One design, endless possibilities! 🧵✂️\n\nZenna exports to:\n• Cross-stitch patterns\n• Embroidery files (PES, DST)\n• Cricut SVGs\n• And more!\n\n🔗 zennago.com\n\n${hashtags}`,
      'testimonial': `"I made this in under a minute!" 🤯\n\nOur users are creating amazing ${image.craftType} patterns with AI.\n\nYour turn → zennago.com\n\n${hashtags}`,
      'free-trial': `🎁 7 FREE designs waiting for you!\n\nNo credit card. No catch. Just creativity.\n\nStart making patterns like this one at zennago.com ✨\n\n${hashtags}`
    }
    
    return captions[template.id] || captions['showcase']
  }

  // =============================================================================
  // DOWNLOAD IMAGE
  // =============================================================================

  const downloadImage = async (image) => {
    try {
      const response = await fetch(image.promoImageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `zenna-promo-${Date.now()}.png`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      // Fallback: open in new tab
      window.open(image.promoImageUrl, '_blank')
    }
  }

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 opacity-80" />
            <span className="text-xs opacity-80">All time</span>
          </div>
          <div className="text-3xl font-bold">{stats.totalPosts}</div>
          <div className="text-sm opacity-80">Total Posts</div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 opacity-80" />
            <span className="text-xs opacity-80">7 days</span>
          </div>
          <div className="text-3xl font-bold">{stats.thisWeek}</div>
          <div className="text-sm opacity-80">This Week</div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <Heart className="w-5 h-5 opacity-80" />
            <span className="text-xs opacity-80">Success rate</span>
          </div>
          <div className="text-3xl font-bold">{stats.totalPosts > 0 ? Math.round((stats.engagement / stats.totalPosts) * 100) : 0}%</div>
          <div className="text-sm opacity-80">Posted Successfully</div>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <Eye className="w-5 h-5 opacity-80" />
            <span className="text-xs opacity-80">Top platform</span>
          </div>
          <div className="text-3xl font-bold capitalize">{stats.topPlatform}</div>
          <div className="text-sm opacity-80">Best Performer</div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 bg-white dark:bg-slate-800 rounded-xl p-1 border border-slate-200 dark:border-slate-700 w-fit">
        {[
          { id: 'generator', label: 'Generate Promos', icon: Sparkles },
          { id: 'templates', label: 'Templates', icon: Layout },
          { id: 'queue', label: 'Post Queue', icon: Clock },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSection === tab.id
                ? 'bg-purple-600 text-white'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Generator Section */}
      {activeSection === 'generator' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Controls */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Generate Branded Promos
            </h3>
            
            {/* Template Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Promo Template
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PROMO_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      selectedTemplate.id === template.id
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-slate-200 dark:border-slate-600 hover:border-purple-500/50'
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-800 dark:text-white">
                      {template.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Craft Type - Only show if NOT multi-craft */}
            {selectedTemplate.id !== 'craft-types' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                  Craft Type
                </label>
                <select
                  value={selectedCraftType}
                  onChange={(e) => setSelectedCraftType(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm"
                >
                  {CRAFT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Multi-Craft Info */}
            {selectedTemplate.id === 'craft-types' && (
              <div className="mb-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <div className="text-sm font-medium text-purple-400 mb-1">
                  🎨 Multi-Craft Mode
                </div>
                <div className="text-xs text-slate-400">
                  Each image will use a different craft type (macrame, stained-glass, knitting, embroidery, etc.)
                </div>
              </div>
            )}
            
            {/* Batch Size */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Generate Count: {batchSize}
              </label>
              <input
                type="range"
                min="1"
                max="8"
                value={batchSize}
                onChange={(e) => setBatchSize(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            
            {/* Custom Prompt (optional) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Custom Prompt (optional)
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Leave empty for random showcase patterns..."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm h-20 resize-none"
              />
            </div>
            
            {/* Generate Button */}
            <button
              onClick={generateBrandedPromos}
              disabled={generating}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {generationStep || 'Generating...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate {batchSize} Branded Promos
                </>
              )}
            </button>
            
            {/* Generation Progress */}
            {generating && generationStep && (
              <div className="mt-3 p-3 bg-purple-500/10 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-purple-400">
                  <Loader className="w-4 h-4 animate-spin" />
                  {generationStep}
                </div>
              </div>
            )}
          </div>

          {/* Generated Images */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-purple-500" />
              Generated Promos
              {generatedImages.length > 0 && (
                <span className="text-xs bg-purple-500/10 text-purple-500 px-2 py-0.5 rounded-full">
                  {generatedImages.length} images
                </span>
              )}
            </h3>
            
            {generatedImages.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Generated promo images will appear here</p>
                <p className="text-sm mt-1">Click generate to create branded promotional content</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {generatedImages.map(image => (
                  <div key={image.id} className="group relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600">
                    <img 
                      src={image.promoImageUrl} 
                      alt={image.prompt}
                      className="w-full aspect-[2/3] object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', image.promoImageUrl)
                        e.target.src = image.patternImageUrl // Fallback to raw pattern
                      }}
                    />
                    
                    {/* Fallback indicator */}
                    {image.isFallback && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-yellow-500/90 rounded-lg text-white text-xs">
                        Raw Pattern
                      </div>
                    )}
                    
                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white text-xs mb-3 line-clamp-2">{image.prompt}</p>
                        
                        {/* Action buttons */}
                        <div className="flex gap-2 flex-wrap">
                          {/* Download */}
                          <button
                            onClick={() => downloadImage(image)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium transition-all"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </button>
                          
                          {/* Post to Facebook */}
                          <button
                            onClick={() => postToSocial(image, 'facebook')}
                            disabled={posting[image.id] === 'facebook'}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all ${
                              postResults[image.id]?.facebook === 'success'
                                ? 'bg-green-500'
                                : postResults[image.id]?.facebook === 'failed'
                                ? 'bg-red-500'
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                          >
                            {posting[image.id] === 'facebook' ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : postResults[image.id]?.facebook === 'success' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : postResults[image.id]?.facebook === 'failed' ? (
                              <XCircle className="w-3 h-3" />
                            ) : (
                              <span className="font-bold">f</span>
                            )}
                            {postResults[image.id]?.facebook === 'success' ? 'Posted!' : 'Facebook'}
                          </button>
                          
                          {/* Post to Pinterest */}
                          <button
                            onClick={() => postToSocial(image, 'pinterest')}
                            disabled={posting[image.id] === 'pinterest'}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all ${
                              postResults[image.id]?.pinterest === 'success'
                                ? 'bg-green-500'
                                : postResults[image.id]?.pinterest === 'failed'
                                ? 'bg-red-500'
                                : 'bg-red-600 hover:bg-red-700'
                            }`}
                          >
                            {posting[image.id] === 'pinterest' ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : postResults[image.id]?.pinterest === 'success' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : postResults[image.id]?.pinterest === 'failed' ? (
                              <XCircle className="w-3 h-3" />
                            ) : (
                              <span className="font-bold">P</span>
                            )}
                            {postResults[image.id]?.pinterest === 'success' ? 'Posted!' : 'Pinterest'}
                          </button>
                          
                          {/* Post to Instagram */}
                          <button
                            onClick={() => postToSocial(image, 'instagram')}
                            disabled={posting[image.id] === 'instagram'}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all ${
                              postResults[image.id]?.instagram === 'success'
                                ? 'bg-green-500'
                                : postResults[image.id]?.instagram === 'failed'
                                ? 'bg-red-500'
                                : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                            }`}
                          >
                            {posting[image.id] === 'instagram' ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : postResults[image.id]?.instagram === 'success' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : postResults[image.id]?.instagram === 'failed' ? (
                              <XCircle className="w-3 h-3" />
                            ) : (
                              <span className="font-bold">IG</span>
                            )}
                            {postResults[image.id]?.instagram === 'success' ? 'Posted!' : 'Instagram'}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Template/Craft type badge */}
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded-lg text-white text-xs">
                      {image.template.id === 'craft-types' ? image.craftType : image.template.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Preview Section */}
      {activeSection === 'templates' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
            Promo Templates Preview
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            These templates add branded text overlays to your generated patterns. The actual promo will include your pattern image with the overlay text.
          </p>
          <div className="grid grid-cols-3 gap-6">
            {PROMO_TEMPLATES.map(template => (
              <div 
                key={template.id} 
                className={`border rounded-xl overflow-hidden cursor-pointer transition-all ${
                  selectedTemplate.id === template.id 
                    ? 'border-purple-500 ring-2 ring-purple-500/20' 
                    : 'border-slate-200 dark:border-slate-600 hover:border-purple-500/50'
                }`}
                onClick={() => setSelectedTemplate(template)}
              >
                {/* Mock Pinterest Pin */}
                <div className="aspect-[2/3] bg-gradient-to-b from-slate-700 to-slate-900 relative">
                  {/* Placeholder image area */}
                  <div className="absolute inset-4 bottom-32 bg-slate-600 rounded-xl flex items-center justify-center">
                    <Image className="w-12 h-12 text-slate-400" />
                  </div>
                  
                  {/* Text overlay area */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900 to-transparent">
                    <div className="text-white font-bold text-lg mb-1">
                      {template.textOverlay}
                    </div>
                    <div className="text-white/80 text-sm">
                      {template.subtext}
                    </div>
                    <div className="text-purple-400 text-xs mt-2">
                      ✨ Made with Zenna AI
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-white dark:bg-slate-800">
                  <h4 className="font-semibold text-slate-800 dark:text-white">{template.name}</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{template.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Queue Section */}
      {activeSection === 'queue' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
            Scheduled Posts Queue
          </h3>
          <div className="text-center py-12 text-slate-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No scheduled posts</p>
            <p className="text-sm mt-1">Scheduling feature coming soon!</p>
          </div>
        </div>
      )}
    </div>
  )
}